// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Huntress — the game's own sound cues, and the list of every cue name it will ever play.
//
// WHY THIS FILE EXISTS: `sfx.play()` on an unknown cue is a deliberate silent no-op in the engine
// (`const p = patches[name]; if (!p) return;`), which is the right call for a mixer but means a cue
// NAME TYPO IS PERMANENTLY INVISIBLE. It cannot throw, jsdom cannot hear it, and a screenshot cannot
// show it. This build shipped `ping('thud')` for the arrow landing — the game's signature moment —
// and for every rejected tap, and `thud` was never a patch: both were silent, and nothing caught it.
//
// So: the custom patches live here, CUES lists every name the game plays, and tests/cues.test.ts
// asserts (a) every name in CUES is known to a real Sfx, and (b) every ping()/sfx.play() literal in
// src/ is in CUES. The second half is what makes the first half load-bearing.

import type { Patch } from '@ben-gy/game-engine/sound';

/**
 * Cues the engine does not ship. Deliberately dry and woody rather than arcade-bright — the board is
 * parchment and ink, and a coin-op blip on an arrow would fight it.
 */
export const PATCHES: Record<string, Patch> = {
  /** The arrow landing. A short, dead thump with a noise transient — a bolt into a board. */
  thud: { type: 'sawtooth', freq: [190, 55], dur: 0.2, gain: 0.3, noise: true },
  /** A huntress lifting off her square. */
  lift: { type: 'triangle', freq: [300, 460], dur: 0.07, gain: 0.16 },
  /** The two sides sealed apart — the moment the board partitions. */
  seal: { type: 'triangle', freq: [220, 110], dur: 0.42, gain: 0.26 },
};

/**
 * EVERY cue name the game plays, engine-built-in or custom. Keep it in sync with the source — the
 * test greps the call sites and fails if a literal is missing from this list.
 */
export const CUES: readonly string[] = [
  // custom
  'thud',
  'lift',
  'seal',
  // engine built-ins the game uses
  'blip',
  'select',
  'win',
  'lose',
  'beat',
  'go',
];
